import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Presets : 12 mois d'abonnement à 29 CHF (offre cachée legacy) ou 49 CHF (tarif négocié).
const PRESETS = [
  { label: "29 CHF × 12 mois", chf: 348, helper: "Offre legacy 29.-/mois" },
  { label: "49 CHF × 12 mois", chf: 588, helper: "Tarif négocié 49.-/mois" },
] as const;

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  establishmentId: string;
  establishmentName: string;
}

export function SendPaymentLinkDialog({
  open,
  onOpenChange,
  establishmentId,
  establishmentName,
}: Props) {
  const [amountChf, setAmountChf] = useState<number>(348);
  const [customMode, setCustomMode] = useState(false);
  const [customChf, setCustomChf] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>(toIsoDate(new Date()));
  const [periodEnd, setPeriodEnd] = useState<string>(toIsoDate(addMonths(new Date(), 12)));
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setAmountChf(348);
      setCustomMode(false);
      setCustomChf("");
      setPeriodStart(toIsoDate(new Date()));
      setPeriodEnd(toIsoDate(addMonths(new Date(), 12)));
      setDescription("");
      setUrl(null);
    }
  }, [open, establishmentId]);

  const finalAmountChf = customMode ? parseFloat(customChf || "0") : amountChf;
  const finalAmountCents = Math.round(finalAmountChf * 100);
  const isAmountValid = finalAmountCents > 0 && finalAmountCents <= 1_000_000;

  const handleGenerate = async () => {
    if (!isAmountValid) {
      toast.error("Montant invalide (1 CHF à 10'000 CHF)");
      return;
    }
    setLoading(true);
    setUrl(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-payment-link", {
        body: {
          establishmentId,
          amountCents: finalAmountCents,
          description: description.trim() || undefined,
          periodStart: periodStart || undefined,
          periodEnd: periodEnd || undefined,
        },
      });
      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      const checkoutUrl = (data as { url?: string })?.url;
      if (!checkoutUrl) throw new Error("Aucune URL retournée");
      setUrl(checkoutUrl);
      toast.success("Lien de paiement généré");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur inconnue";
      toast.error(`Erreur : ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Envoyer un lien de paiement Stripe</DialogTitle>
          <DialogDescription>
            Établissement : <strong>{establishmentName}</strong>. Paiement one-time
            (mode=payment). Pas de subscription Stripe — facturation annuelle d'un
            coup. Les remboursements éventuels se font à la main depuis le Dashboard Stripe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Montant */}
          <div>
            <Label>Montant</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {PRESETS.map((p) => (
                <Button
                  key={p.chf}
                  type="button"
                  variant={!customMode && amountChf === p.chf ? "default" : "outline"}
                  size="sm"
                  className="h-auto py-2 flex-col items-start"
                  onClick={() => {
                    setCustomMode(false);
                    setAmountChf(p.chf);
                  }}
                >
                  <span className="text-base font-semibold">{p.chf} CHF</span>
                  <span className="text-[10px] opacity-70">{p.helper}</span>
                </Button>
              ))}
              <Button
                type="button"
                variant={customMode ? "default" : "outline"}
                size="sm"
                className="h-auto py-2 flex-col items-center justify-center"
                onClick={() => setCustomMode(true)}
              >
                <span className="text-base font-semibold">Autre</span>
                <span className="text-[10px] opacity-70">Montant libre</span>
              </Button>
            </div>
            {customMode && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  step={1}
                  placeholder="Montant en CHF"
                  value={customChf}
                  onChange={(e) => setCustomChf(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">CHF</span>
              </div>
            )}
          </div>

          {/* Période */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Début période</Label>
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label>Fin période</Label>
              <Input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          {/* Description optionnelle */}
          <div>
            <Label>Description (optionnelle)</Label>
            <Textarea
              placeholder={`Par défaut : "Abonnement Piktable — ${establishmentName} (${periodStart} → ${periodEnd})"`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[60px]"
            />
          </div>

          {/* URL générée */}
          {url && (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <Label>Lien Stripe</Label>
              <div className="flex gap-2">
                <Input readOnly value={url} className="font-mono text-xs" />
                <Button size="icon" variant="outline" onClick={handleCopy} title="Copier">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="outline" asChild title="Ouvrir">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Envoie ce lien au client. Le webhook Stripe marquera le paiement comme
                payé dans stripe_payments automatiquement.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {url ? "Fermer" : "Annuler"}
          </Button>
          {!url && (
            <Button onClick={handleGenerate} disabled={loading || !isAmountValid}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Génération…
                </>
              ) : (
                `Générer (${finalAmountChf || 0} CHF)`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
